<template>
  <div class="indoorviewer-menu-search-result">
    <NavigationMenuItem
      :closeable="true"
      :title="$t('general__text__search-results')"
      @close="$emit('close')"
    >
      <div v-if="searchResults.length !== 0" class="indoorviewer-menu-search-result__container">
        <ListRow
          v-if="searchResults[0].length === 0 && searchResults[1].length === 0"
          :clickable="false"
          :title="noSearchResultsString"
        />
        <ListRow
          v-for="poi in searchResults[0]"
          :key="poi.id"
          :imgSource="getFullUrl(poi)"
          :title="poi.title"
          :position="
            isBuildingSelected
              ? $t('general__text__room') +
                ' ' +
                poi.room +
                ' | ' +
                $t('general__text__level') +
                ' ' +
                poi.floor
              : ''
          "
          tabindex="0"
          @click="selectPoi(poi)"
          @keydown.enter="selectPoi(poi)"
          @keydown.space="(event) => handleSpaceKey(poi, event, 'selectPoi')"
        />
        <div v-for="(array, idx) in extendedSearchResults" :key="idx">
          <ListRow
            v-for="poi in array"
            :key="poi.id"
            :imgSource="getFullUrl(poi)"
            :title="poi.title"
            :position="
              isBuildingSelected
                ? $t('general__text__room') +
                  ' ' +
                  poi.room +
                  ' | ' +
                  $t('general__text__level') +
                  ' ' +
                  poi.floor
                : ''
            "
            tabindex="0"
            @click="selectPoi(poi)"
            @keydown.enter="selectPoi(poi)"
            @keydown.space="(event) => handleSpaceKey(poi, event, 'selectPoi')"
          />
        </div>
        <ListRow
          v-if="searchResults[0].length >= 6"
          :title="addMoreSearchResultsString"
          :imgSource="iconExtendSearch"
          tabindex="0"
          @click="extendSearch(15)"
          @keydown.enter="extendSearch(15)"
          @keydown.space="(event) => handleSpaceKey(15, event, 'extendSearch')"
        />
        <ListRow
          v-for="room in searchResults[1]"
          :key="room.id"
          :imgSource="iconPoiRoom"
          :title="$t('general__text__room') + ' ' + room.name"
          :position="$t('general__text__level') + ' ' + room.floor"
          tabindex="0"
          @click="selectRoom(room)"
          @keydown.enter="selectRoom(room)"
          @keydown.space="(event) => handleSpaceKey(room, event, 'selectRoom')"
        />
      </div>
    </NavigationMenuItem>
  </div>
</template>

<script>
import ListRow from '@/components/general/navigation/ListRow.vue'
import NavigationMenuItem from '@/components/general/navigation/NavigationMenuItem.vue'
import { INDOORVIEWER_PREFIX, MAPVIEW_PREFIX, PLANVIEW_PREFIX } from '@/store/module-prefixes'
import { CHANGE_POI } from '@/store/action-types'
import { SET_CLICKED_ITEM_TYPE, SET_CURRENT_ROOM } from '@/store/mutation-types'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import { goToRoom, goToPOI, navvisPoiSearchRequest } from '@/services/navvis.service'
import { getPoiWithAddedProperties } from '@/utils/data'
import { ivApi } from '../IndoorviewerView.vue'
import iconExtendSearch from '@/assets/poi/extend-search.svg'
import iconPoiRoom from '@/assets/poi/room.png'
export default {
  name: 'IndoorviewerMenuSearchResult',
  components: {
    NavigationMenuItem,
    ListRow
  },
  props: {
    searchResults: {
      type: Array,
      required: true
    },
    searchString: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  data() {
    return {
      extendedSearchFrom: 7,
      extendedSearchResults: [],
      iconExtendSearch,
      iconPoiRoom
    }
  },
  computed: {
    addMoreSearchResultsString() {
      return 'Visa fler POI-sökresultat'
    },
    noSearchResultsString() {
      return 'Inga sökresultat för ' + this.searchString
    },
    ...mapGetters(INDOORVIEWER_PREFIX, {
      navvisInstanceURL: 'navvisInstanceURL'
    }),
    ...mapState(INDOORVIEWER_PREFIX, {
      navvisApiToken: (state) => state.navvisApiToken,
      poisForSelectedBuildingIvion: (state) => state.poisForSelectedBuildingIvion,
      poisForSelectedRealEstateIvion: (state) => state.poisForSelectedRealEstateIvion
    }),
    ...mapState(MAPVIEW_PREFIX, {
      selectedBuilding: (state) => state.selectedBuilding,
      poiCategoriesForSelectedRealEstate: (state) => state.poiCategoriesForSelectedRealEstate
    }),
    ...mapState(PLANVIEW_PREFIX, {
      poiCategoriesForSelectedLevel: (state) => state.poiCategoriesForSelectedLevel
    }),
    isBuildingSelected() {
      return Object.keys(this.selectedBuilding).length > 0
    }
  },
  watch: {
    searchString() {
      this.extendedSearchFrom = 7
      this.extendedSearchResults = []
    }
  },
  methods: {
    async extendSearch(numberofpois) {
      let extendedResults = await navvisPoiSearchRequest(
        this.navvisInstanceURL,
        this.searchString,
        this.extendedSearchFrom,
        numberofpois,
        this.navvisApiToken
      )
      this.extendedSearchFrom = this.extendedSearchFrom + numberofpois
      this.extendedSearchResults.push(extendedResults)
    },
    handleSpaceKey(value, event, type) {
      event.preventDefault()
      if (type === 'selectPoi') this.selectPoi(value)
      else if (type === 'selectRoom') this.selectRoom(value)
      else if (type === 'extendSearch') this.extendSearch(value)
    },
    async selectPoi(poi) {
      let poiBackend = this.isBuildingSelected
        ? this.poisForSelectedBuildingIvion.find(
            (poiBackend) => poiBackend.properties.navVisId === poi.id // For pois only: navVisId equals id instead of siteModelEntityId on backend
          )
        : this.poisForSelectedRealEstateIvion.find(
            (poiBackend) => poiBackend.properties.navVisId === poi.id // For pois only: navVisId equals id instead of siteModelEntityId on backend
          )
      if (poiBackend) {
        poiBackend = getPoiWithAddedProperties(
          poiBackend,
          this.isBuildingSelected
            ? this.poiCategoriesForSelectedLevel
            : this.poiCategoriesForSelectedRealEstate
        )
      }
      if (!poiBackend) {
        const fullNavVisPoi = await ivApi.poi.repository.findOne(poi.id)
        if (fullNavVisPoi) {
          poi.descriptions = {}
          if (Object.keys(fullNavVisPoi.descriptions).length > 0) {
            const poiDescription = fullNavVisPoi.descriptions.sv
              ? fullNavVisPoi.descriptions.sv
              : fullNavVisPoi.descriptions.en
            poi.descriptions.sv = poiDescription
              .replace(/<img.*<\/img>/g, '') // Removing image tag due to BIMobject issues (old connection for validated pois, for example in AWL)
              .replace(/<a.*<\/a>/g, '') // Excluding links due to BIMobject issues (old connection for validated pois, for example in AWL)
            poi.descriptions.en = poi.descriptions.sv
            poi.description = poi.descriptions.sv
          }
          poi.title = fullNavVisPoi.titles.sv
            ? fullNavVisPoi.titles.sv
            : fullNavVisPoi.titles.en
            ? fullNavVisPoi.titles.en
            : ''
          poi.scs_location = fullNavVisPoi.location
          poi.location = poi.scs_location
        }
      }
      this.dispatchPoi({ poi: poiBackend ? poiBackend.properties : poi }) // Using backend poi if matching id:s
      this.commitClick({ type: 'poi' })
      goToPOI({ poi: poi })
    },
    selectRoom(room) {
      // Using data from navvis.service.js before going towards our own API
      room.properties = {
        belongsToId: room.id,
        displayName: room.name,
        externalId: room.name,
        id: room.id,
        level: room.floor,
        location: room.center,
        name: room.name,
        nta: room.area,
        type: 'SPACE'
      }
      room.externalId = room.name
      room.geometry = room.polygon
      room.level = room.floor
      room.nta = room.area
      room.type = 'FEATURE'
      // Can the room information display be activated with information from navvis? Or should we create a separate one?
      /*
      this.commitRoom({ room: room })
      this.commitClick({ type: 'room' })
      */
      // Re-connect check for panorama images below?
      /*
      if (checkForPanoramaImages(room.id)) { // Checks if the room (siteModelEntityId) has images and only moves if true. Import from navvis.service if used again
        goToRoom(room.id)
      }
      */
      goToRoom(room.id) // Todo: Inform the user if the room is outside of the panorama and the view will switch to point cloud?
    },
    getFullUrl(poi) {
      if (poi?.icon === null) return ''
      let navvisPois = poi.icon.includes('poi_navvis')
      // Info for future development:
      // The navvisInstanceURL (incloding Ivion site id) doesn't seem to work for the icons
      // When removing the ?site=... part of the url, the standard icons are shown
      // But the custom icons are not shown no matter which url is used
      return navvisPois
        ? this.navvisInstanceURL + poi.icon
        : this.navvisInstanceURL + poi.icon + '?x_authorization=' + this.navvisApiToken
    },
    ...mapActions({
      dispatchPoi: CHANGE_POI
    }),
    ...mapMutations({
      commitClick: SET_CLICKED_ITEM_TYPE,
      commitRoom: SET_CURRENT_ROOM
    })
  }
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/mixins.scss';
@use '@/styles/variables.scss' as old-variables;
.indoorviewer-menu-search-result__container {
  background-color: old-variables.$color-grey-blue-dark;
  border-radius: old-variables.$border-radius-medium;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: old-variables.$max-height-navigation-menu-container-extended;
  @include mixins.scrollbar-vertical;
}
</style>
